<template>
    <dynamic-element
        v-if="previewMode || isMinimal"
        :id="renderId"
        v-bind="renderProps"
        :tag="tagNew"
        :class="renderClasses"
        :style="renderStyles"
        v-on="$listeners"
        @click="onClick"
        @mouseenter="onMouseenter"
        @mouseleave="onMouseleave"
        @submit="onSubmit"
        @change="onChange"
    >
        <slot />
    </dynamic-element>

    <dynamic-element
        v-else
        :id="renderId"
        v-bind="renderProps"
        :tag="tagNew"
        :class="renderClasses"
        :style="renderStyles"
        v-on="$listeners"
        @click.prevent.stop="$parent.onClick"
        @click.prevent.stop.right
        @dblclick.stop="$parent.onDoubleClick"
    >
        <transition appear name="toolbar-fade">
            <toolbar-guided-select
                v-if="showGuidedToolbar"
                :display-name="displayName"
                :element="$parent"
            />
        </transition>

        <box-model :visible="isBoxModelVisible" />

        <slot />
    </dynamic-element>
</template>

<script>
import { mapState } from 'vuex';

import ToolbarGuidedSelect from '@/components/page-editor/toolbars/toolbar-guided-select';
import DynamicElement from '@/components/utilities/dynamic-element';
import BoxModel from '@/components/shared/box-model/box-model';
import { STATE_CLASSES } from '@/data/constants/classes';
import { tailwindConfig } from '@/data/tailwind';
import listenOnRootMixin from '@/mixins/listen-on-root';
import flexEventMixin from '@/mixins/flex-event';
import { getClassDefinition } from '@/utils/tailwind-helper';
import {
    backgroundImageStyle,
    gradientStyleByElement,
    floatingNavStyle, // only used on xccom
    offsetStyle,
    textShadowStyle,
} from '@/utils/css';

export default {
    components: {
        BoxModel,
        DynamicElement,
        ToolbarGuidedSelect,
    },

    mixins: [flexEventMixin, listenOnRootMixin],

    props: {
        tag: {
            type: String,
            default: 'div',
        },

        // changes the root element to an anchor, and adds link-related options
        isLinkDefault: {
            type: Boolean,
            default: false,
        },

        // the flex class group(s) you'd like to prevent from being applied on the element
        // e.g. pass ['textColor'] to prevent any color classes (f-text-black, f-text-white, etc) from being applied
        // see @/data/tailwind.js for more details about flex class groups
        excludeClasses: {
            type: [Array, String],
            default: () => [],
        },
    },

    data() {
        return {
            editorClasses: [], // editor only classes
            positioningClasses: '',
            spacing: 4, // @TODO - update this to be dynamic
            tagNew: this.tag,
            isValid: true,
        };
    },

    computed: {
        ...mapState({
            deviceMode: state => state.page.deviceMode,
            guidedMode: state => state.page.guidedMode,
            previewMode: state => state.previewMode,
        }),

        isMinimal() {
            return this.$parent.$vnode.componentOptions.tag.endsWith('-minimal');
        },

        displayName() {
            return this.$parent.getElementData('displayName')
                || this.$parent.editorName
                || this.$parent.displayName;
        },

        htmlClass() {
            let classes = this.$parent.htmlClass;

            if (Array.isArray(this.excludeClasses)) {
                // exclude only certain classes
                const disabledFlexClassGroups = [].concat(this.excludeClasses);

                classes = classes.filter((className) => {
                    const classDefinition = getClassDefinition(className);
                    const { tailwindDefinitionProp } = classDefinition;

                    return !disabledFlexClassGroups.includes(tailwindDefinitionProp);
                });
            } else if (typeof this.excludeClasses === 'string') {
                // n.b. Vue passes an empty string when no prop value is set
                // exclude all classes
                classes = [];
            }

            if (this.$parent.isReadOnly) {
                classes.push(STATE_CLASSES.STATIC);
            }

            // remove any negative z-indexes or overflow:hidden so the toolbar can display properly
            if (this.isToolbarVisible) {
                classes = classes.filter(className => className !== 'f-overflow-hidden');
            }

            // remove any fixed positioning classes if in editing mode
            if (!this.previewMode) {
                classes = classes.filter(className => !className.includes('fu-placement') && !className.includes('fu-placement-fixed'));
            }

            return classes.concat(this.editorClasses);
        },

        stateClasses() {
            return {
                [STATE_CLASSES.DISABLED]: this.$parent.hasElementData('isDisabled'),
                [STATE_CLASSES.INVALID]: !this.isValid,
            };
        },

        renderId() {
            return this.$parent.idOverride || this.$parent.id;
        },

        renderProps() {
            const linkProps = this.$parent.isLinkNew
                ? {
                    href: this.$parent.renderHref,
                    target: this.$parent.renderTarget,
                    title: this.$parent.renderTitle,
                    rel: this.$parent.renderRel,
                    targetItemId: this.$parent.renderTargetItemId,
                }
                : {};

            const ariaLabelProp = this.$parent.ariaLabel ? { 'aria-label': this.$parent.ariaLabel } : '';
            const ariaLabelledByProp = this.$parent.ariaLabelledBy ? { 'aria-labelledby': this.$parent.ariaLabelledBy } : '';
            const ariaHiddenProp = this.$parent.ariaHidden ? { 'aria-hidden': true } : '';
            const accessibilityRole = this.$parent.accessibilityRole ? { role: this.$parent.accessibilityRole } : '';

            return { ...linkProps, ...this.$parent.htmlCustomAttributes, ...ariaLabelProp, ...ariaLabelledByProp, ...ariaHiddenProp, ...accessibilityRole };
        },

        renderClasses() {
            return [this.htmlClass, this.positioningClasses, this.stateClasses];
        },

        renderStyles() {
            const hasViewportHeight = this.$parent.htmlClass.includes('f-min-h-screen');

            const styleObject = {
                ...backgroundImageStyle(this.$parent),
                ...floatingNavStyle(this.$parent),
                ...offsetStyle(this.$parent),
                ...textShadowStyle(this.$parent),

                // excluding a-image as it handles gradient styles differently
                ...(this.$parent.elementType !== 'a-image' ? gradientStyleByElement(this.$parent) : {}),

                marginTop: this.guidedMode
                    ? '30px !important'
                    : '',

                // this mimics 100vh within the context of the tablet and mobile devices
                minHeight: this.previewMode
                    && hasViewportHeight
                    && this.deviceMode
                    && (this.deviceMode.name === 'tab' || this.deviceMode.name === 'mob') ? '100%' : '',
            };

            return styleObject;
        },


        showGuidedToolbar() {
            return this.$parent.isHighlighted && !this.previewMode;
        },

        isBoxModelVisible() {
            return this.$parent.boxModelDisplayed
                && this.$parent.elementType !== 'flex-layout';
        },

        isToolbarVisible() {
            return this.showElementToolbar || this.showGuidedToolbar;
        },
    },

    watch: {
        '$parent.boxModelDisplayed': async function boxModelWatcher(isDisplayed) {
            if (isDisplayed) {
                this.resetPositioningClasses();
                await this.$nextTick();
                this.setPositioningClasses();
            } else {
                this.resetPositioningClasses();
            }
        },

        '$parent.isLinkNew': function isLinkWatcher(convertToLink) {
            if (convertToLink) {
                this.setAsLink();
            } else {
                this.tagNew = this.tag;
            }
        },

        // should only fire on initial set
        '$parent.mounted': function parentMounted() {
            if (this.$parent.isLink) {
                this.setAsLink();
            }
        },

        '$parent.isSelected': function parentSelected(selected) {
            const editorClassSet = new Set(this.editorClasses);

            if (selected) {
                editorClassSet.add(STATE_CLASSES.SELECTED_ELEMENT);
            } else {
                editorClassSet.delete(STATE_CLASSES.SELECTED_ELEMENT);
            }

            this.editorClasses = Array.from(editorClassSet);
        },

        tag() {
            this.tagNew = this.tag;
        },

        '$parent.isValid': function onParentValidation() {
            this.isValid = this.$parent.isValid;
        },

        async isToolbarVisible(isDisplayed) {
            if (isDisplayed) {
                this.resetPositioningClasses();
                await this.$nextTick();
                this.setPositioningClasses();
            } else {
                this.resetPositioningClasses();
            }
        },
    },

    created() {
        this.listenOnRoot('flex::preset::update', this.setPositioningClasses);
        this.isValid = this.$parent.isValid;
    },

    beforeMount() {
        if (this.isLinkDefault) this.$parent.isLinkNew = true;
    },

    methods: {
        setAsLink() {
            const { classList } = this.$parent.$el;
            const computedDisplay = window.getComputedStyle(this.$parent.$el).getPropertyValue('display');

            this.tagNew = 'a';

            if (classList.contains('f-flex')
                || classList.contains('f-block')
                || classList.contains('f-inline-block')) {
                return;
            }

            if (computedDisplay === 'flex'
                || computedDisplay === 'block'
                || computedDisplay === 'inline-block') {
                return;
            }

            this.$parent.flexClassCollection.display.all.default = 'f-block';
        },

        setPositioningClasses() {
            const existingClasses = this.$parent.htmlClass.concat(this.$parent.$el.className.split(' '));
            const positionClasses = Object.values(tailwindConfig.theme.position).map(val => `f-${ val }`);
            const placementClasses = Object.keys(tailwindConfig.theme.placement).map(key => `fu-placement-${ key }`);

            const hasPositionClass = !!existingClasses.filter(className => positionClasses.includes(className)).length;
            const hasPlacementClass = !!existingClasses.filter(className => placementClasses.includes(className)).length;

            const newClasses = [];

            if (!hasPositionClass && !hasPlacementClass) {
                newClasses.push('f-relative');
            }

            this.positioningClasses = newClasses;
        },

        resetPositioningClasses() {
            this.positioningClasses = [];
        },
    },
};
</script>
